if (!customElements.get('infinite-scroll')) {
  class InfiniteScroll extends HTMLElement {
    constructor() {
      super();

      this.initialized = false;
      this.isLoading = false;
      this.isRestoring = false;

      this.nextUrl = null;
      this.observer = null;
      this.abortController = null;

      this.handleIntersection =
        this.handleIntersection.bind(this);

      this.handleButtonClick =
        this.handleButtonClick.bind(this);

      this.handleProductClick =
        this.handleProductClick.bind(this);

      this.handlePageHide =
        this.handlePageHide.bind(this);

      this.handlePageShow =
        this.handlePageShow.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.pagination = this.querySelector(
        '.infinite-scroll__pagination'
      );

      this.status = this.querySelector(
        '.infinite-scroll__status'
      );

      this.button = this.querySelector(
        '.infinite-scroll__button'
      );

      this.productGrid = document.querySelector(
        '#product-grid'
      );

      const sentinelId =
        this.getAttribute('sentinel-id');

      this.sentinel = sentinelId
        ? document.getElementById(sentinelId)
        : null;

      this.perPage =
        Number.parseInt(
          this.getAttribute('per-page'),
          10
        ) || 24;

      this.productSelector =
        this.getAttribute('product-selector') ||
        '[product-id]';

      if (
        !this.pagination ||
        !this.productGrid ||
        !this.sentinel
      ) {
        console.warn(
          'Infinite scroll could not initialize.',
          {
            pagination: this.pagination,
            productGrid: this.productGrid,
            sentinel: this.sentinel,
          }
        );

        return;
      }

      this.initialized = true;

      this.nextUrl =
        this.getNextUrlFromPagination(
          this.pagination
        );

      this.button?.addEventListener(
        'click',
        this.handleButtonClick
      );

      this.productGrid.addEventListener(
        'click',
        this.handleProductClick
      );

      window.addEventListener(
        'pagehide',
        this.handlePageHide
      );

      window.addEventListener(
        'pageshow',
        this.handlePageShow
      );

      this.restoreFromUrl();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.abortController?.abort();

      this.button?.removeEventListener(
        'click',
        this.handleButtonClick
      );

      this.productGrid?.removeEventListener(
        'click',
        this.handleProductClick
      );

      window.removeEventListener(
        'pagehide',
        this.handlePageHide
      );

      window.removeEventListener(
        'pageshow',
        this.handlePageShow
      );

      this.initialized = false;
    }

    async restoreFromUrl() {
      const requestedSize =
        this.getRequestedProductCount();

      const currentSize =
        this.getVisibleProductCount();

      if (requestedSize <= currentSize) {
        this.initializeObserver();
        return;
      }

      this.isRestoring = true;
      this.setAttribute('restoring', '');

      this.observer?.disconnect();

      const savedState = this.getSavedState();

      try {
        while (
          this.getVisibleProductCount() <
            requestedSize &&
          this.nextUrl
        ) {
          const loaded = await this.loadNextPage({
            updateHistory: false,
            announce: false,
          });

          if (!loaded) break;
        }
      } catch (error) {
        console.error(
          'Infinite-scroll restoration failed:',
          error
        );
      } finally {
        this.isRestoring = false;
        this.removeAttribute('restoring');

        await this.waitForLayout();

        this.restorePosition(savedState);
        this.initializeObserver();
      }
    }

    initializeObserver() {
      this.observer?.disconnect();

      if (!this.nextUrl) {
        this.finish();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        if (this.button) {
          this.button.hidden = false;
        }

        return;
      }

      this.observer = new IntersectionObserver(
        this.handleIntersection,
        {
          root: null,
          rootMargin: '800px 0px',
          threshold: 0,
        }
      );

      this.observer.observe(this.sentinel);
    }

    handleIntersection(entries) {
      if (
        this.isLoading ||
        this.isRestoring ||
        !this.nextUrl
      ) {
        return;
      }

      const shouldLoad = entries.some(
        entry => entry.isIntersecting
      );

      if (shouldLoad) {
        this.loadNextPage();
      }
    }

    handleButtonClick() {
      this.loadNextPage();
    }

    async loadNextPage({
      updateHistory = true,
      announce = true,
    } = {}) {
      if (this.isLoading || !this.nextUrl) {
        return false;
      }

      this.isLoading = true;
      this.setAttribute('loading', '');

      if (announce) {
        this.updateStatus(
          'Loading more products…'
        );
      }

      const requestedUrl = this.nextUrl;

      try {
        const result = await this.fetchPage(
          requestedUrl
        );

        if (!result || !result.items.length) {
          this.finish();
          return false;
        }

        const fragment =
          document.createDocumentFragment();

        /*
         * Append every direct child from the incoming grid.
         * This includes product cards and disruptor tiles.
         */
        result.items.forEach(item => {
          fragment.appendChild(item);
        });

        this.productGrid.appendChild(fragment);

        this.nextUrl = result.nextUrl;
        
        if (updateHistory) {
          this.updateUrlState();
        }

        this.dispatchEvent(
          new CustomEvent('products:loaded', {
            bubbles: true,
            detail: {
              items: result.items,
              page: result.page,
              productCount:
                this.getVisibleProductCount(),
              nextUrl: result.nextUrl,
            },
          })
        );

        if (announce) {
          this.updateStatus(
            `${result.productCount} additional product${
              result.productCount === 1
                ? ''
                : 's'
            } loaded.`
          );
        }

        if (!this.nextUrl) {
          this.finish();
        }

        return true;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error(
            'Infinite-scroll request failed:',
            error
          );

          this.updateStatus(
            'More products could not be loaded.'
          );

          if (this.button) {
            this.button.hidden = false;
          }
        }

        return false;
      } finally {
        this.isLoading = false;
        this.removeAttribute('loading');
      }
    }

    async fetchPage(url) {
      this.abortController?.abort();

      this.abortController =
        new AbortController();

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(
          `Request failed with status ${response.status}`
        );
      }

      const html = await response.text();

      const parsedDocument =
        new DOMParser().parseFromString(
          html,
          'text/html'
        );

      const incomingGrid =
        parsedDocument.querySelector(
          '#product-grid'
        );

      const incomingInfiniteScroll =
        parsedDocument.querySelector(
          'infinite-scroll'
        );

      const incomingPagination =
        incomingInfiniteScroll?.querySelector(
          '.infinite-scroll__pagination'
        );

      if (!incomingGrid) {
        return null;
      }

      /*
       * Every direct grid child is appended. This preserves
       * product cards, disruptors, promotional tiles, etc.
       */
      const items = [
        ...incomingGrid.children,
      ];

      return {
        items,
        page: this.getPageNumber(url),

        /*
         * Count only real products, not disruptor tiles.
         */
        productCount:
          this.countProductsInItems(items),

        nextUrl:
          this.getNextUrlFromPagination(
            incomingPagination
          ),
      };
    }

    updateUrlState() {
      const size =
        this.getVisibleProductCount();

      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        'products.size',
        size
      );

      /*
       * The current DOM contains page one through the most
       * recently loaded page, rather than one isolated page.
       */
      url.searchParams.delete('page');

      const currentState = {
        ...(history.state || {}),
        infiniteScroll: {
          ...(history.state?.infiniteScroll ||
            {}),
          size,
          scrollY: window.scrollY,
        },
      };

      history.replaceState(
        currentState,
        '',
        `${url.pathname}${url.search}${url.hash}`
      );
    }

    handleProductClick(event) {
      const productElement =
        event.target.closest(
          this.productSelector
        );

      if (
        !productElement ||
        !this.productGrid.contains(productElement)
      ) {
        return;
      }

      const productId =
        productElement.getAttribute(
          'product-id'
        ) ||
        productElement.dataset.productId;

      if (!productId) return;

      const gridItem =
        productElement.closest(
          '#product-grid > *'
        ) || productElement;

      const state = {
        size: this.getVisibleProductCount(),
        productId,
        scrollY: window.scrollY,

        /*
         * Save the card's position relative to the viewport,
         * which is more stable than only saving scrollY.
         */
        viewportOffset:
          gridItem.getBoundingClientRect().top,

        savedAt: Date.now(),
      };

      this.saveState(state);

      history.replaceState(
        {
          ...(history.state || {}),
          infiniteScroll: state,
        },
        '',
        window.location.href
      );
    }

    handlePageHide() {
      const previousState =
        this.getSavedState() || {};

      const state = {
        ...previousState,
        size: this.getVisibleProductCount(),
        scrollY: window.scrollY,
        savedAt: Date.now(),
      };

      this.saveState(state);

      history.replaceState(
        {
          ...(history.state || {}),
          infiniteScroll: state,
        },
        '',
        window.location.href
      );
    }

    handlePageShow(event) {
      if (!event.persisted) return;

      /*
       * The browser restored the expanded DOM through
       * back/forward cache. Do not rebuild the product grid
       * or manually restore the scroll position.
       */
      this.isLoading = false;
      this.isRestoring = false;

      this.removeAttribute('loading');
      this.removeAttribute('restoring');

      this.initializeObserver();
    }

    restorePosition(state) {
      if (!state) return;

      const productElement =
        state.productId
          ? this.findProductById(
              state.productId
            )
          : null;

      if (productElement) {
        const gridItem =
          productElement.closest(
            '#product-grid > *'
          ) || productElement;

        const absoluteTop =
          gridItem.getBoundingClientRect().top +
          window.scrollY;

        const viewportOffset =
          Number.isFinite(
            state.viewportOffset
          )
            ? state.viewportOffset
            : 0;

        window.scrollTo({
          top: Math.max(
            0,
            absoluteTop - viewportOffset
          ),
          behavior: 'auto',
        });

        return;
      }

      if (
        Number.isFinite(state.scrollY)
      ) {
        window.scrollTo({
          top: state.scrollY,
          behavior: 'auto',
        });
      }
    }

    findProductById(productId) {
      const escapedProductId =
        CSS.escape(String(productId));

      return this.productGrid.querySelector(
        `[product-id="${escapedProductId}"]`
      );
    }

    getVisibleProductCount() {
      return this.getProductGridItems()
        .filter(item =>
          item.matches(
            this.productSelector
          ) ||
          item.querySelector(
            this.productSelector
          )
        )
        .length;
    }

    getProductGridItems() {
      return [
        ...this.productGrid.children,
      ];
    }

    countProductsInItems(items) {
      return items.filter(item =>
        item.matches?.(
          this.productSelector
        ) ||
        item.querySelector?.(
          this.productSelector
        )
      ).length;
    }

    getRequestedProductCount() {
      const url = new URL(
        window.location.href
      );

      const requested =
        Number.parseInt(
          url.searchParams.get(
            'products.size'
          ),
          10
        );

      if (
        !Number.isFinite(requested) ||
        requested < 1
      ) {
        return (
          this.getVisibleProductCount() ||
          this.perPage
        );
      }

      return requested;
    }

    getNextUrlFromPagination(
      pagination
    ) {
      if (!pagination) return null;

      const relNext =
        pagination.querySelector(
          'a[rel="next"]'
        );

      if (relNext?.href) {
        return relNext.href;
      }

      const currentElement =
        pagination.querySelector(
          '[aria-current="page"]'
        );

      const currentPage =
        Number.parseInt(
          currentElement?.textContent
            ?.trim() || '1',
          10
        );

      const links = [
        ...pagination.querySelectorAll(
          'a[href]'
        ),
      ];

      const nextPageLink =
        links.find(link => {
          return (
            this.getPageNumber(
              link.href
            ) === currentPage + 1
          );
        });

      return nextPageLink?.href || null;
    }

    getPageNumber(urlValue) {
      try {
        const url = new URL(
          urlValue,
          window.location.origin
        );

        const page =
          Number.parseInt(
            url.searchParams.get(
              'page'
            ) || '1',
            10
          );

        return (
          Number.isFinite(page) &&
          page > 0
        )
          ? page
          : 1;
      } catch {
        return 1;
      }
    }

    getStorageKey() {
      const url = new URL(
        window.location.href
      );

      /*
       * Keep collection filters, sorting and search state,
       * but remove infinite-scroll-only parameters.
       */
      url.searchParams.delete('page');
      url.searchParams.delete(
        'products.size'
      );

      const normalizedSearch = [
        ...url.searchParams.entries(),
      ]
        .sort(([keyA], [keyB]) =>
          keyA.localeCompare(keyB)
        )
        .map(([key, value]) => {
          return `${encodeURIComponent(
            key
          )}=${encodeURIComponent(
            value
          )}`;
        })
        .join('&');

      return [
        'infinite-scroll',
        url.pathname,
        normalizedSearch,
      ].join(':');
    }

    saveState(state) {
      try {
        sessionStorage.setItem(
          this.getStorageKey(),
          JSON.stringify(state)
        );
      } catch (error) {
        console.warn(
          'Unable to save infinite-scroll state:',
          error
        );
      }
    }

    getSavedState() {
      const historyState =
        history.state?.infiniteScroll;

      if (historyState) {
        return historyState;
      }

      try {
        const storedValue =
          sessionStorage.getItem(
            this.getStorageKey()
          );

        return storedValue
          ? JSON.parse(storedValue)
          : null;
      } catch {
        return null;
      }
    }

    waitForLayout() {
      return new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(
            resolve
          );
        });
      });
    }

    updateStatus(message) {
      if (this.status) {
        this.status.textContent =
          message;
      }
    }

    finish() {
      this.nextUrl = null;

      this.observer?.disconnect();

      this.setAttribute(
        'complete',
        ''
      );

      if (this.button) {
        this.button.hidden = true;
      }

      this.updateStatus(
        'All products have been loaded.'
      );
    }
  }

  customElements.define(
    'infinite-scroll',
    InfiniteScroll
  );
}